import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Download, FileSpreadsheet, Package, CheckCircle, AlertTriangle, DollarSign, ArrowUpDown, MessageCircle, Store,
} from "lucide-react";
import { toast } from "sonner";
import LojaSelector from "@/components/LojaSelector";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useLojas, useLojasInfo } from "@/hooks/useLojas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const MESES = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const fmt = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const fmtDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("pt-BR");
};

export default function RelatorioLentes() {
  const LOJAS_GATE = useLojas();
  const { empresa: empresaNome } = useLojasInfo();
  const hoje = new Date();
  const navigate = useNavigate();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [loja, setLoja] = useState("Todas as lojas");
  const [dados, setDados] = useState([]);
  const [resumo, setResumo] = useState({
    total_pedidos: 0, total_valor: 0, em_producao: 0, finalizados: 0, atrasados: 0, pgto_pendente: 0,
  });
  const [loading, setLoading] = useState(true);
  const [aba, setAba] = useState("todos");

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const params = { mes, ano };
      if (loja && loja !== "Todas as lojas") params.loja = loja;
      const res = await axios.get(`${API}/relatorio/lentes`, { params });
      setDados(res.data.dados);
      setResumo(res.data.resumo);
    } catch {
      toast.error("Erro ao carregar relatorio");
    } finally {
      setLoading(false);
    }
  }, [mes, ano, loja]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  const filtrarPorAba = (lista, abaSelecionada) => {
    switch (abaSelecionada) {
      case "finalizados":
        return lista.filter((r) => r.status === "Pronto para Entrega");
      case "em_producao":
        return lista.filter((r) => (r.status || "Em Produção") === "Em Produção");
      case "atrasados":
        return lista.filter((r) => r.atrasado);
      case "pgto_pendente":
        return lista.filter((r) => r.pgto_pendente);
      default:
        return lista;
    }
  };

  const dadosFiltrados = filtrarPorAba(dados, aba);

  const toggleStatus = async (r) => {
    const novoStatus = r.status === "Em Produção" ? "Pronto para Entrega" : "Em Produção";
    try {
      await axios.patch(`${API}/receitas/${r.id}/status?status=${encodeURIComponent(novoStatus)}`);
      toast.success(`Status: ${novoStatus === "Pronto para Entrega" ? "Finalizado" : "Em Producao"}`);
      carregarDados();
    } catch {
      toast.error("Erro ao alterar status");
    }
  };

  const atualizarPrazo = async (id, dias) => {
    try {
      await axios.patch(`${API}/relatorio/receita/${id}/prazo?prazo_dias=${dias}`);
      carregarDados();
    } catch {
      toast.error("Erro ao atualizar prazo");
    }
  };

  const atualizarFaltaPagamento = async (id, valor, formaQuitacao = null) => {
    try {
      const params = new URLSearchParams({ valor });
      if (formaQuitacao) params.append("forma_quitacao", formaQuitacao);
      const res = await axios.patch(`${API}/relatorio/receita/${id}/falta-pagamento?${params.toString()}`);
      if (res.data.quitado) {
        toast.success(`Pagamento quitado via ${formaQuitacao || ""}!`);
      } else {
        toast.success("Valor pendente atualizado");
        setAba("pgto_pendente");
      }
      carregarDados();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const quitarPagamento = async (id, { valor, data, forma }) => {
    try {
      const params = new URLSearchParams({ valor: String(valor), forma });
      if (data) params.append("data", data);
      const res = await axios.post(`${API}/relatorio/receita/${id}/quitar?${params.toString()}`);
      if (res.data.quitado) {
        toast.success(`Pagamento quitado via ${forma}!`);
      } else {
        toast.success(`Parcial de ${fmt(res.data.valor_quitado)} recebido. Saldo: ${fmt(res.data.saldo_pendente)}`);
      }
      carregarDados();
    } catch {
      toast.error("Erro ao registrar quitação");
    }
  };

  const exportarCSV = () => {
    if (dadosFiltrados.length === 0) return;
    const BOM = "\uFEFF";
    const sep = ";";
    const header = [
      "Status", "OS", "Nome do Cliente", "Telefone", "Laboratorio",
      "Lente Feita", "Data da Compra", "Prazo", "Valor da Lente (R$)",
      "Falta Pagamento", "Loja",
    ];
    const rows = dadosFiltrados.map((r) => [
      r.status === "Pronto para Entrega" ? "Finalizado" : "Em Producao",
      r.os || "-",
      r.nome_cliente || "-",
      r.telefone_cliente || "-",
      r.nome_laboratorio || "-",
      r.tipo_lente || "-",
      fmtDate(r.data_registro),
      fmtDate(r.prazo_data),
      (r.valor_lente || 0).toFixed(2).replace(".", ","),
      r.falta_pagamento || "",
      r.nome_loja || "-",
    ]);
    const totalValor = dadosFiltrados.reduce((s, r) => s + (r.valor_lente || 0), 0);
    rows.push([]);
    rows.push(["", "", "", "", "", "", "", "TOTAL:", totalValor.toFixed(2).replace(".", ","), "", ""]);

    const csv = BOM + [header, ...rows].map((r) => r.join(sep)).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const suffixes = { finalizados: "_finalizados", em_producao: "_em_producao", atrasados: "_atrasados", pgto_pendente: "_pgto_pendente" };
    link.download = `relatorio_lentes_${MESES[mes - 1]}_${ano}${suffixes[aba] || ""}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("Relatorio exportado");
  };

  const anos = [];
  for (let a = 2024; a <= hoje.getFullYear() + 1; a++) anos.push(a);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // desc = mais novo primeiro
  const [sortField, setSortField] = useState("data"); // data ou os
  const [lojaSelecionadaGate, setLojaSelecionadaGate] = useState(false);

  // Evita ternários aninhados nos botões de ordenação
  const rotuloSort = (campo, desc, asc, inativo) => {
    if (sortField !== campo) return inativo;
    return sortOrder === "desc" ? desc : asc;
  };

  const dadosPesquisados = dadosFiltrados.filter(r => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (r.os || "").toLowerCase().includes(s) || (r.nome_cliente || "").toLowerCase().includes(s);
  });

  const dadosOrdenados = [...dadosPesquisados].sort((a, b) => {
    if (sortField === "os") {
      // Ordenar por número da OS
      const osA = parseInt((a.os || "0").replace(/\D/g, "")) || 0;
      const osB = parseInt((b.os || "0").replace(/\D/g, "")) || 0;
      return sortOrder === "desc" ? osB - osA : osA - osB;
    } else {
      // Ordenar por data
      const da = a.data_registro || "";
      const db2 = b.data_registro || "";
      return sortOrder === "desc" ? db2.localeCompare(da) : da.localeCompare(db2);
    }
  });

  const mensagemVazia = () => {
    if (searchTerm) return "Nenhum resultado para a pesquisa";
    const msgs = {
      finalizados: "Nenhum pedido finalizado neste periodo",
      em_producao: "Nenhum pedido em producao neste periodo",
      atrasados: "Nenhuma lente atrasada neste periodo",
      pgto_pendente: "Nenhum pagamento pendente neste periodo",
    };
    return msgs[aba] || `Nenhum pedido encontrado para ${MESES[mes - 1]} ${ano}`;
  };

  if (!lojaSelecionadaGate) {
    const opcoes = [
      { value: "Todas as lojas", label: "Gerencial (todas as lojas)", gerencial: true },
      ...LOJAS_GATE.map((l) => ({ value: l, label: l })),
    ];
    const escolher = (v) => { setLoja(v); setLojaSelecionadaGate(true); };
    return (
      <div className="max-w-2xl mx-auto" data-testid="relatorio-loja-gate">
        <div className="text-center mb-8 mt-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-white mb-4"><Store className="w-7 h-7" /></div>
          <h1 className="text-3xl font-bold text-slate-900">Relatorio de Lentes — Selecione a loja</h1>
          <p className="text-sm text-slate-500 mt-1">Escolha uma loja para ver o relatorio ou entre no modo gerencial</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {opcoes.map((o) => (
            <button key={o.value} onClick={() => escolher(o.value)}
              data-testid={`relatorio-gate-${o.value.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left hover:shadow-md ${
                o.gerencial ? "border-slate-900 bg-slate-900 text-white sm:col-span-2 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
              }`}>
              <Store className={`w-5 h-5 shrink-0 ${o.gerencial ? "text-white" : "text-slate-500"}`} />
              <span className="font-semibold">{o.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="relatorio-lentes-page">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="relatorio-title">
          Relatorio de Lentes
        </h1>
        <p className="text-sm text-slate-500">
          Planilha de pedidos — {MESES[mes - 1]} {ano}
        </p>
      </div>

      {/* Filters */}
      <Card className="shadow-sm border-slate-200 bg-white">
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">Mes</label>
              <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
                <SelectTrigger className="w-36 h-9" data-testid="filtro-mes"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MESES.map((nome, i) => (
                    <SelectItem key={nome} value={String(i + 1)}>{nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">Ano</label>
              <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
                <SelectTrigger className="w-24 h-9" data-testid="filtro-ano"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {anos.map((a) => (
                    <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">Loja</label>
              <LojaSelector lojaSelecionada={loja} onLojaChange={setLoja} />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-slate-500">Pesquisar</label>
              <Input
                placeholder="Buscar por OS ou nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9"
                data-testid="relatorio-search"
              />
            </div>
            <button
              onClick={() => {
                if (sortField === "data") {
                  setSortOrder(sortOrder === "desc" ? "asc" : "desc");
                } else {
                  setSortField("data");
                  setSortOrder("desc");
                }
              }}
              className={`h-9 px-3 text-xs font-medium rounded border transition-colors flex items-center gap-1.5 ${
                sortField === "data" 
                  ? "bg-slate-700 text-white border-slate-700 hover:bg-slate-800" 
                  : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300"
              }`}
              title={rotuloSort("data", "Mais novo primeiro", "Mais antigo primeiro", "Ordenar por data")}
              data-testid="sort-date-toggle"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              {rotuloSort("data", "Recente", "Antigo", "Data")}
            </button>
            <button
              onClick={() => {
                if (sortField === "os") {
                  setSortOrder(sortOrder === "desc" ? "asc" : "desc");
                } else {
                  setSortField("os");
                  setSortOrder("desc");
                }
              }}
              className={`h-9 px-3 text-xs font-medium rounded border transition-colors flex items-center gap-1.5 ${
                sortField === "os" 
                  ? "bg-slate-700 text-white border-slate-700 hover:bg-slate-800" 
                  : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300"
              }`}
              title={rotuloSort("os", "OS maior primeiro", "OS menor primeiro", "Ordenar por OS")}
              data-testid="sort-os-toggle"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              {rotuloSort("os", "OS ↓", "OS ↑", "OS")}
            </button>
            <Button onClick={exportarCSV} variant="outline" disabled={dadosFiltrados.length === 0}
              className="active:scale-95 transition-transform h-9" data-testid="export-csv-button">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryCard label="Total Pedidos" value={resumo.total_pedidos} testid="total-pedidos"
          className="bg-white border-slate-200" />
        <SummaryCard label="Valor Total" value={fmt(resumo.total_valor)} testid="total-valor"
          className="bg-white border-slate-200" />
        <SummaryCard label="Finalizados" value={resumo.finalizados} testid="total-finalizados"
          className="border-emerald-200 bg-emerald-50/60" labelClass="text-emerald-600" valueClass="text-emerald-700" />
        <SummaryCard label="Em Producao" value={resumo.em_producao} testid="total-producao"
          className="border-red-200 bg-red-50/60" labelClass="text-red-600" valueClass="text-red-700" />
        <SummaryCard label="Atrasados" value={resumo.atrasados} testid="total-atrasados"
          className="border-orange-300 bg-orange-50/60" labelClass="text-orange-600" valueClass="text-orange-700" />
        <SummaryCard label="Pgto Pendente" value={resumo.pgto_pendente} testid="total-pgto-pendente"
          className="border-violet-300 bg-violet-50/60" labelClass="text-violet-600" valueClass="text-violet-700" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        <TabBtn active={aba === "todos"} onClick={() => setAba("todos")} testid="tab-todos"
          color="slate">{`Todos (${resumo.total_pedidos})`}</TabBtn>
        <TabBtn active={aba === "finalizados"} onClick={() => setAba("finalizados")} testid="tab-finalizados"
          color="emerald" icon={<CheckCircle className="w-3.5 h-3.5" />}>{`Finalizado (${resumo.finalizados})`}</TabBtn>
        <TabBtn active={aba === "em_producao"} onClick={() => setAba("em_producao")} testid="tab-em-producao"
          color="red" icon={<Package className="w-3.5 h-3.5" />}>{`Em Producao (${resumo.em_producao})`}</TabBtn>
        <TabBtn active={aba === "atrasados"} onClick={() => setAba("atrasados")} testid="tab-atrasados"
          color="orange" icon={<AlertTriangle className="w-3.5 h-3.5" />}>{`Atrasadas (${resumo.atrasados})`}</TabBtn>
        <TabBtn active={aba === "pgto_pendente"} onClick={() => setAba("pgto_pendente")} testid="tab-pgto-pendente"
          color="violet" icon={<DollarSign className="w-3.5 h-3.5" />}>{`Pgto Pendente (${resumo.pgto_pendente})`}</TabBtn>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner message="Carregando relatorio..." />
      ) : dadosOrdenados.length === 0 ? (
        <Card className="shadow-sm bg-white" data-testid="empty-relatorio">
          <CardContent className="py-12 text-center">
            <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">
              {mensagemVazia()}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-sm border-slate-200" data-testid="relatorio-table-card">
          <div className="overflow-x-auto overflow-y-visible">
            <Table data-testid="relatorio-table" className="text-xs">
              <TableHeader>
                <TableRow className="bg-slate-800 hover:bg-slate-800">
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[100px]">Status</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[60px]">OS</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2">Cliente</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[70px]">Loja</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[100px]">Telefone</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[90px]">Lab</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[90px]">Lente</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[75px]">Data</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[110px]">Prazo</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[80px] text-right">Valor</TableHead>
                  <TableHead className="text-slate-100 font-semibold px-2 py-2 w-[170px]">Falta Pgto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dadosOrdenados.map((r, idx) => (
                  <ReportRow key={r.id || idx} r={r} idx={idx}
                    onToggleStatus={toggleStatus} onPrazoChange={atualizarPrazo}
                    onFaltaPgtoChange={atualizarFaltaPagamento} onQuitar={quitarPagamento} navigate={navigate} />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow className="bg-slate-100 hover:bg-slate-100 font-bold">
                  <TableCell colSpan={9} className="text-right text-slate-700 font-bold text-xs px-2">
                    TOTAL ({dadosOrdenados.length} pedidos)
                  </TableCell>
                  <TableCell className="text-right text-slate-900 font-bold text-xs px-2" data-testid="footer-total">
                    {fmt(dadosOrdenados.reduce((s, r) => s + (r.valor_lente || 0), 0))}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({ label, value, testid, className = "", labelClass = "text-slate-500", valueClass = "text-slate-900" }) {
  return (
    <Card className={`shadow-sm border-slate-200 ${className}`}>
      <CardContent className="pt-4 pb-3 px-4">
        <p className={`text-xs ${labelClass}`}>{label}</p>
        <p className={`text-xl font-bold ${valueClass}`} data-testid={testid}>{value}</p>
      </CardContent>
    </Card>
  );
}

function TabBtn({ active, onClick, testid, color, icon, children }) {
  const colors = {
    slate: active ? "border-slate-900 text-slate-900" : "",
    emerald: active ? "border-emerald-500 text-emerald-600" : "",
    red: active ? "border-red-500 text-red-600" : "",
    orange: active ? "border-orange-500 text-orange-600" : "",
    violet: active ? "border-violet-500 text-violet-600" : "",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap ${
        active ? colors[color] : "border-transparent text-slate-400 hover:text-slate-600"
      }`}>
      {icon}{children}
    </button>
  );
}

function ReportRow({ r, idx, onToggleStatus, onPrazoChange, onFaltaPgtoChange, onQuitar, navigate }) {
  const { empresa: empresaNome } = useLojasInfo();
  const parseValor = (val) => {
    if (!val) return "";
    const limpo = String(val).replace("R$", "").replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
    const num = parseFloat(limpo);
    return isNaN(num) || num <= 0 ? "" : num.toFixed(2);
  };

  const [faltaLocal, setFaltaLocal] = useState(parseValor(r.falta_pagamento));
  const hojeStr = new Date(Date.now() - 3 * 3600 * 1000).toISOString().split("T")[0];
  const [showQuitar, setShowQuitar] = useState(false);
  const [quitarData, setQuitarData] = useState(hojeStr);
  const [quitarValor, setQuitarValor] = useState("");
  const [quitarForma, setQuitarForma] = useState("PIX");
  const finalizado = r.status === "Pronto para Entrega";
  const temPendente = faltaLocal !== "" && parseFloat(faltaLocal) > 0;

  const FORMAS_QUITAR = ["PIX", "Dinheiro", "Crédito", "Débito", "Crediário"];

  const salvarFalta = (novoValor) => {
    const valorStr = novoValor === "" || parseFloat(novoValor) <= 0 ? "" : novoValor;
    onFaltaPgtoChange(r.id, valorStr);
  };

  const abrirQuitar = () => {
    setQuitarValor(faltaLocal || "");
    setQuitarData(hojeStr);
    setQuitarForma("PIX");
    setShowQuitar(true);
  };

  const confirmarQuitacao = () => {
    const val = parseFloat(quitarValor);
    if (!val || val <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    onQuitar(r.id, { valor: val, data: quitarData, forma: quitarForma });
    setShowQuitar(false);
  };

  const abrirWhatsApp = () => {
    const digits = String(r.telefone_cliente || "").replace(/\D/g, "");
    if (!digits) {
      toast.error("Cliente sem telefone cadastrado");
      return;
    }
    const numero = digits.startsWith("55") ? digits : `55${digits}`;
    const msg = `Ola${r.nome_cliente ? " " + r.nome_cliente : ""}! Passando para avisar que o seu oculos${r.os ? ` (OS ${r.os})` : ""} ja esta pronto para retirada${empresaNome ? " na " + empresaNome : ""}. Aguardamos a sua visita!`;
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const getRowBg = () => {
    if (finalizado) return "bg-emerald-50 hover:bg-emerald-100/80 border-emerald-100";
    if (r.atrasado) return "bg-orange-50 hover:bg-orange-100/80 border-orange-200";
    return "bg-red-50 hover:bg-red-100/80 border-red-100";
  };
  const rowBg = getRowBg();

  return (
    <TableRow className={rowBg} data-testid={`relatorio-row-${r.id || idx}`}>
      <TableCell className="px-2 py-1.5">
        {r.is_adicional ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700" data-testid={`lente-extra-${r.id || idx}`}>
            Lente extra
          </span>
        ) : (
          <button onClick={() => onToggleStatus(r)}
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full cursor-pointer transition-opacity hover:opacity-75 ${
              finalizado ? "bg-emerald-200 text-emerald-800" : "bg-red-200 text-red-800"
            }`}
            title="Clique para alternar status" data-testid={`status-toggle-${r.id || idx}`}>
            {finalizado ? <CheckCircle className="w-3 h-3" /> : <Package className="w-3 h-3" />}
            {finalizado ? "Pronto" : "Producao"}
          </button>
        )}
        {r.atrasado && !finalizado && (
          <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded bg-orange-200 text-orange-800"
            data-testid={`badge-atrasado-${r.id || idx}`}>
            <AlertTriangle className="w-2.5 h-2.5" /> ATRASADO
          </span>
        )}
      </TableCell>
      <TableCell className="font-mono font-medium text-slate-900 text-xs px-2 py-1.5">{r.os || "-"}</TableCell>
      <TableCell className="px-2 py-1.5">
        <button onClick={() => navigate(`/receitas/editar/${r.receita_id || r.id}`)}
          className="font-medium text-slate-900 hover:text-blue-700 hover:underline cursor-pointer text-left text-xs"
          title="Abrir receita" data-testid={`link-cliente-${r.id || idx}`}>
          {r.nome_cliente || "-"}
        </button>
      </TableCell>
      <TableCell className="text-slate-600 text-xs px-2 py-1.5">{r.nome_loja || "-"}</TableCell>
      <TableCell className="text-slate-600 text-xs px-2 py-1.5">
        {r.telefone_cliente ? (
          <div className="flex items-center gap-1.5">
            <span>{r.telefone_cliente}</span>
            <button
              onClick={abrirWhatsApp}
              title="Avisar cliente pelo WhatsApp que o oculos esta pronto"
              data-testid={`btn-whatsapp-${r.id || idx}`}
              className="inline-flex items-center justify-center h-6 w-6 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors shrink-0"
            >
              <MessageCircle className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : "-"}
      </TableCell>
      <TableCell className="text-slate-600 text-xs px-2 py-1.5">{r.nome_laboratorio || "-"}</TableCell>
      <TableCell className="text-slate-700 text-xs px-2 py-1.5">{r.tipo_lente || "-"}</TableCell>
      <TableCell className="text-slate-700 text-xs px-2 py-1.5">{fmtDate(r.data_registro)}</TableCell>
      <TableCell className="px-2 py-1.5">
        <div className="flex items-center gap-1">
          <span className={`text-xs ${r.atrasado && !finalizado ? "text-orange-700 font-semibold" : "text-slate-600"}`}>
            {fmtDate(r.prazo_data)}
          </span>
          {!r.is_adicional && (
          <Select value={String(r.prazo_dias || 7)} onValueChange={(v) => onPrazoChange(r.id, Number(v))}>
            <SelectTrigger className="h-6 w-12 text-[10px] px-1 border-slate-300" data-testid={`prazo-select-${r.id || idx}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[3, 5, 7, 10].map((d) => (
                <SelectItem key={d} value={String(d)}>{d}d</SelectItem>
              ))}
            </SelectContent>
          </Select>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right font-medium text-slate-900 text-xs px-2 py-1.5">{fmt(r.valor_lente)}</TableCell>
      <TableCell className="px-2 py-1.5">
        {r.is_adicional ? (
          <span className="text-xs text-slate-400">—</span>
        ) : (
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1 min-w-[90px]">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">R$</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={faltaLocal}
              onChange={(e) => setFaltaLocal(e.target.value)}
              onBlur={() => {
                if (faltaLocal !== parseValor(r.falta_pagamento)) salvarFalta(faltaLocal);
              }}
              placeholder="0,00"
              className={`h-8 text-sm pl-8 pr-2 border-slate-300 ${temPendente ? "bg-amber-50 border-amber-400 text-amber-800 font-semibold" : "bg-white/70"}`}
              data-testid={`falta-pgto-input-${r.id || idx}`}
            />
          </div>
          {temPendente && (
            <>
              <button
                onClick={abrirQuitar}
                className="h-8 px-2.5 text-xs font-bold bg-emerald-100 text-emerald-700 rounded border border-emerald-300 hover:bg-emerald-200 transition-colors whitespace-nowrap"
                title="Registrar quitação (data, valor e forma)"
                data-testid={`btn-quitar-${r.id || idx}`}
              >
                Quitar
              </button>
              <Dialog open={showQuitar} onOpenChange={setShowQuitar}>
                <DialogContent className="sm:max-w-md bg-white" data-testid={`quitar-modal-${r.id || idx}`}>
                  <DialogHeader>
                    <DialogTitle>Quitar pagamento pendente</DialogTitle>
                    <DialogDescription>
                      {r.nome_cliente || "Cliente"} — Pendente: {fmt(parseFloat(faltaLocal) || 0)}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-slate-700">Data da quitação</Label>
                      <Input type="date" value={quitarData} onChange={(e) => setQuitarData(e.target.value)}
                        data-testid={`quitar-data-${r.id || idx}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-slate-700">Valor a quitar (R$)</Label>
                      <Input type="number" step="0.01" min="0" value={quitarValor}
                        onChange={(e) => setQuitarValor(e.target.value)} placeholder="0,00"
                        data-testid={`quitar-valor-${r.id || idx}`} />
                      <p className="text-xs text-slate-500">Se for menor que o pendente, o saldo continua em aberto e segue contando o prazo.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-slate-700">Forma de pagamento</Label>
                      <Select value={quitarForma} onValueChange={setQuitarForma}>
                        <SelectTrigger data-testid={`quitar-forma-${r.id || idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FORMAS_QUITAR.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowQuitar(false)} data-testid={`quitar-cancelar-${r.id || idx}`}>Cancelar</Button>
                    <Button onClick={confirmarQuitacao} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`quitar-confirmar-${r.id || idx}`}>Confirmar</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
        )}
      </TableCell>
    </TableRow>
  );
}
